import React from 'react';
import { SkeletonProps } from '../../../types/DesignSystem/Skeleton';
import { withClassName } from '../cn';

/**
 * Skeleton — a shape standing in for content that has not arrived.
 *
 * `Table` already advertised a `loading` prop before this existed, and rendered the
 * literal string `Loading...` in a translucent overlay for it. A skeleton is the
 * better answer for a table specifically: it holds the row height, so the page does
 * not jump when data lands, and it says *what kind* of thing is coming.
 *
 * **Deliberately `aria-hidden`.** A skeleton is decoration; the wait itself belongs
 * to one live region, not to fifteen shimmering rectangles. `Table` announces via
 * `role="status"` and `aria-busy` on the container. A skeleton that announced itself
 * would make a screen reader read the same "loading" fifteen times.
 */

const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton({
  variant = 'text',
  width,
  height,
  lines = 1,
  wave = 0,
  className = '',
  'data-testid': dataTestId,
  style,
  ...rest
}, ref) {
  /*
    A gradient SWEEP, not a pulse (2026-08-25). `animate-pulse` fades the whole
    shape in place — functional, but it reads as blinking. The sweep moves a
    lighter band across the shape, which reads as work happening. Under
    prefers-reduced-motion the animation is gated off and what remains is the
    static gradient — a quiet grey shape, no movement, same geometry.

    § 295 — the stops are the Skeleton's OWN roles. They were `edge → surface-sunken
    → edge`, which is lighter-in-the-middle in light (L* 88.7 → 96.1) and darker-in-
    the-middle in dark (21.6 → 5.5, `surface-sunken` being the dark page ground) — so
    every dark loading table was swept by a shadow. `skeleton-base` / `skeleton-sheen`
    hold "the band is lighter than the bar" in both themes; light is byte-identical.
  */
  const base = 'tw-bg-gradient-to-r tw-from-skeleton-base tw-via-skeleton-sheen tw-to-skeleton-base '
    + 'tw-bg-[length:200%_100%] motion-safe:tw-animate-shimmer';
  /*
    § 223 — the wave. A NEGATIVE delay, so an offset element never sits static
    waiting for its turn: it starts mid-sweep, exactly as if it had been running
    all along. 120ms per step against the 1.8s period reads as one band crossing
    a table diagonally; `0` emits no style at all, so a lone skeleton is
    byte-identical to what it was.
  */
  const WAVE_STEP_MS = 120;
  const phase = (index: number): React.CSSProperties | undefined =>
    index > 0 ? { animationDelay: `-${index * WAVE_STEP_MS}ms` } : undefined;
  const shape = {
    // Matches `body-primary`'s 20px line box, so a skeleton line occupies exactly
    // the height the real text will.
    text: 'tw-h-3 tw-rounded-sm',
    rect: 'tw-rounded-ctl',
    circle: 'tw-rounded-full',
  }[variant];

  if (variant === 'text' && lines > 1) {
    return (
      <div
        {...rest}
        ref={ref}
        aria-hidden="true"
        className={withClassName(`tw-flex tw-flex-col tw-gap-2`, className)}
        data-testid={dataTestId}
        style={style}
      >
        {Array.from({ length: lines }, (_, i) => (
          <div
            key={i}
            className={`${base} ${shape}`}
            // Last line short, the way a real paragraph ends. Uniform bars read as
            // a loading bar, not as text. Each line rides the wave one step after
            // the one above it, on top of whatever phase the caller passed.
            style={{ width: i === lines - 1 ? '60%' : (width ?? '100%'), ...phase(wave + i) }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      {...rest}
      ref={ref}
      aria-hidden="true"
      className={withClassName(`${base} ${shape}`, className)}
      data-testid={dataTestId}
      style={{ width: width ?? '100%', height, ...phase(wave), ...style }}
    />
  );
});

export default Skeleton;
